import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Copy,
  Download,
  Eye,
  FilePlus2,
  FolderInput,
  GitBranch,
  Mail,
  PackageSearch,
  Pencil,
  Plus,
  Search,
  Trash2,
  Truck,
} from "lucide-react";
import client from "../api/client";
import Modal from "../Moodboard/Modal";
import { ProductPicker, downloadFile, productName } from "../Architect/common";
import { formatMoney, formatSize } from "../Architect/units";
import CustomerDialog from "./CustomerDialog";
import { StatusBadge } from "./QuotationList";
import { DEFAULT_GST, GST_RATES, HSN, STATE_OPTIONS, UNITS, formatDate, stateLabel } from "./gst";
import "../Moodboard/Moodboard.css";
import "../Architect/Architect.css";
import "./Quotations.css";

const SAVE_DELAY = 600;
const UNIT_FOR = { area: "sq ft", roll: "roll", panel: "panel", piece: "pcs" };
const STATUS_TEXT = { saved: "All changes saved", saving: "Saving...", error: "Not saved", invalid: "Fix the highlighted fields", conflict: "Changed elsewhere" };

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `l${Date.now()}${Math.random().toString(16).slice(2)}`);
const text = (value) => (value === null || value === undefined ? "" : String(value));

// The editable part of a quotation, with numbers kept as typed.
const docFrom = (quote) => ({
  customer_id: quote.customer_id,
  subject: quote.subject || "",
  quote_date: quote.quote_date,
  valid_until: quote.valid_until || "",
  place_of_supply: quote.place_of_supply || "",
  discount_type: quote.discount_type,
  discount_value: text(quote.discount_value || ""),
  round_off: quote.round_off,
  notes: quote.notes || "",
  terms: quote.terms || "",
  lines: (quote.lines || []).map((line) => ({
    ...line,
    qty: text(line.qty),
    rate: text(line.rate),
    discount_pct: line.discount_pct ? text(line.discount_pct) : "",
    gst_pct: text(line.gst_pct),
  })),
});

const isNumber = (value, low, high) => {
  if (value === "" || value === null) return true;
  const number = Number(value);
  return Number.isFinite(number) && number >= low && number <= high;
};

// Problems the server would refuse, caught before saving.
function problemsIn(doc) {
  const lines = {};
  doc.lines.forEach((line) => {
    const bad = [];
    if (line.hsn && !HSN.test(line.hsn)) bad.push("hsn");
    if (!isNumber(line.qty, 0, 1000000)) bad.push("qty");
    if (!isNumber(line.rate, 0, 1000000000)) bad.push("rate");
    if (!isNumber(line.discount_pct, 0, 100)) bad.push("discount_pct");
    if (bad.length) lines[line.id] = bad;
  });
  const quote = [];
  if (!isNumber(doc.discount_value, 0, doc.discount_type === "percent" ? 100 : 1000000000)) quote.push("discount");
  if (doc.valid_until && doc.quote_date && doc.valid_until < doc.quote_date) quote.push("valid_until");
  if (!doc.quote_date) quote.push("quote_date");
  return { lines, quote, any: Object.keys(lines).length > 0 || quote.length > 0 };
}

function newLine(kind = "item") {
  return { id: uid(), kind, file_id: null, name: kind === "charge" ? "" : "", description: "", code: "", room: "", hsn: "", unit: kind === "charge" ? "job" : "nos", qty: "1", rate: "", discount_pct: "", gst_pct: String(DEFAULT_GST) };
}

function lineFromProduct(item, details) {
  const parts = [];
  if (details.sold_as !== "area") {
    const size = formatSize(details.width_mm, details.height_mm, "ft");
    if (size) parts.push(`${details.sold_as === "roll" ? "Roll" : "Size"} ${size}`);
  }
  const finish = [details.material, details.finish].filter(Boolean).join(", ");
  if (finish) parts.push(finish);
  return {
    ...newLine("item"),
    file_id: item.file_id,
    name: productName(item),
    code: details.code || "",
    description: parts.join(" · "),
    hsn: details.hsn || "",
    unit: UNIT_FOR[details.sold_as] || "nos",
    rate: details.price != null ? String(details.price) : "",
    gst_pct: String(details.gst_pct ?? DEFAULT_GST),
  };
}

function CustomerPicker({ value, onPick, disabled }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      client
        .get("/api/admin/customers", { params: { search: search || undefined } })
        .then(({ data }) => setRows(data.items || []))
        .catch(() => setRows([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [search, open]);
  return (
    <div className="qt-combobox">
      <label className="ar-search">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          type="search"
          value={search}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={value ? "Change customer" : "Search customers"}
          aria-label="Search customers"
        />
      </label>
      {open && (
        <ul className="qt-combobox-list" role="listbox">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" role="option" aria-selected={row.id === value} onMouseDown={(event) => event.preventDefault()} onClick={() => { setOpen(false); setSearch(""); onPick(row); }}>
                <strong>{row.company || row.name}</strong>
                <span>{[row.company ? row.name : null, stateLabel(row.state_code), row.gstin].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
          <li>
            <button type="button" className="qt-combobox-new" onMouseDown={(event) => event.preventDefault()} onClick={() => { setOpen(false); setCreating(true); }}>
              <Plus size={14} strokeWidth={2} aria-hidden="true" /> New customer
            </button>
          </li>
        </ul>
      )}
      {creating && (
        <CustomerDialog
          onClose={() => setCreating(false)}
          onSaved={(customer) => {
            setCreating(false);
            onPick(customer);
          }}
        />
      )}
    </div>
  );
}

function ImportProjectDialog({ onClose, onImport }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      client
        .get("/api/admin/projects", { params: { search: search || undefined } })
        .then(({ data }) => setRows(data))
        .catch(() => setRows([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);
  return (
    <Modal title="Import products from a project" onClose={onClose}>
      <div className="ar-form">
        <p className="ar-muted ar-small">Lines come in at JUMIG's catalogue prices with the project's quantities and rooms.</p>
        <label className="ar-search">
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Project, client or architect" autoFocus />
        </label>
        {rows == null ? (
          <p className="ar-muted">Loading...</p>
        ) : (
          <ul className="ar-project-choices">
            {rows.map((row) => (
              <li key={row.id}>
                <button type="button" onClick={() => onImport(row.id)}>
                  <strong>{row.name}</strong>
                  <span>{[row.owner, row.client_name].filter(Boolean).join(" · ")}</span>
                </button>
              </li>
            ))}
            {!rows.length && <p className="ar-muted">No projects found.</p>}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function EmailDialog({ quote, onClose, onSent }) {
  const customer = quote.customer || {};
  const company = quote.company || {};
  const [form, setForm] = useState({
    to: customer.email || "",
    cc: "",
    subject: `Quotation ${quote.display_number} from ${company.company_name || "JUMIG Store"}`,
    message: `Thank you for your enquiry. Please find attached our quotation${quote.subject ? ` for ${quote.subject}` : ""}.\n\nWe look forward to working with you.`,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const send = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await client.post(`/api/admin/quotations/${quote.id}/email`, form, { timeout: 120000 });
      onSent(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not send the email.");
      setBusy(false);
    }
  };
  return (
    <Modal title={`Email ${quote.display_number}`} onClose={onClose} wide>
      <form className="ar-form" onSubmit={send}>
        {quote.status === "draft" && <p className="ar-warning ar-small">Sending marks the quotation as sent and locks it.</p>}
        <div className="ar-grid-2">
          <label className="ar-field">
            <span className="ar-field-label">To (separate several with commas)</span>
            <input type="text" value={form.to} onChange={set("to")} required />
          </label>
          <label className="ar-field">
            <span className="ar-field-label">Cc</span>
            <input type="text" value={form.cc} onChange={set("cc")} />
          </label>
        </div>
        <label className="ar-field">
          <span className="ar-field-label">Subject</span>
          <input type="text" value={form.subject} maxLength={200} onChange={set("subject")} />
        </label>
        <label className="ar-field">
          <span className="ar-field-label">Message</span>
          <textarea rows={6} value={form.message} maxLength={5000} onChange={set("message")} />
        </label>
        <p className="ar-muted ar-small">The quotation PDF is attached. Replies go to {company.email || "the address in Company details"}.</p>
        {error && <p className="ar-warning">{error}</p>}
        <div className="mb-modal-actions">
          <button type="button" className="mb-text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="mb-primary" disabled={busy || !form.to.trim()}>
            <Mail size={15} strokeWidth={1.9} aria-hidden="true" />
            {busy ? "Sending..." : "Send quotation"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LineRow({ line, index, total, computed, invalid, disabled, onChange, onMove, onRemove }) {
  const set = (key) => (event) => onChange({ [key]: event.target.value });
  const bad = (key) => (invalid?.includes(key) ? " is-invalid" : "");
  return (
    <div className={`qt-line${line.kind === "charge" ? " is-charge" : ""}`}>
      <span className="qt-line-no">{index + 1}</span>
      <div className="qt-line-item">
        <div className="qt-line-name">
          {line.kind === "charge" && <span className="qt-charge-tag">Charge</span>}
          <input type="text" value={line.name} maxLength={200} onChange={set("name")} disabled={disabled} placeholder={line.kind === "charge" ? "e.g. Installation, freight" : "Item name"} aria-label="Item name" />
        </div>
        <textarea rows={1} value={line.description} maxLength={600} onChange={set("description")} disabled={disabled} placeholder="Description (size, material, finish...)" aria-label="Description" />
        <div className="qt-line-extra">
          <input type="text" value={line.code} maxLength={40} onChange={set("code")} disabled={disabled} placeholder="Code" aria-label="Code" />
          <input type="text" value={line.room} maxLength={120} onChange={set("room")} disabled={disabled} placeholder="Room / area" aria-label="Room" />
        </div>
      </div>
      <label className={`qt-cell${bad("hsn")}`}>
        <span>HSN/SAC</span>
        <input type="text" inputMode="numeric" value={line.hsn} maxLength={8} onChange={set("hsn")} disabled={disabled} />
      </label>
      <label className={`qt-cell${bad("qty")}`}>
        <span>Qty</span>
        <input type="number" min={0} step="any" value={line.qty} onChange={set("qty")} disabled={disabled} />
      </label>
      <label className="qt-cell">
        <span>Unit</span>
        <input type="text" list="qt-units" value={line.unit} maxLength={20} onChange={set("unit")} disabled={disabled} />
      </label>
      <label className={`qt-cell${bad("rate")}`}>
        <span>Rate (INR)</span>
        <input type="number" min={0} step="0.01" value={line.rate} onChange={set("rate")} disabled={disabled} />
      </label>
      <label className={`qt-cell${bad("discount_pct")}`}>
        <span>Disc. %</span>
        <input type="number" min={0} max={100} step="0.01" value={line.discount_pct} onChange={set("discount_pct")} disabled={disabled} placeholder="0" />
      </label>
      <label className="qt-cell">
        <span>GST</span>
        <select value={line.gst_pct} onChange={set("gst_pct")} disabled={disabled}>
          {GST_RATES.map((rate) => (
            <option key={rate} value={String(rate)}>
              {rate}%
            </option>
          ))}
        </select>
      </label>
      <div className="qt-cell qt-amount">
        <span>Taxable</span>
        <strong>{computed ? formatMoney(computed.taxable) : "—"}</strong>
      </div>
      {!disabled && (
        <div className="qt-line-actions">
          <button type="button" className="mb-icon-button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up" title="Move up">
            <ArrowUp size={13} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move down" title="Move down">
            <ArrowDown size={13} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button danger" onClick={onRemove} aria-label="Remove line" title="Remove line">
            <Trash2 size={13} strokeWidth={1.8} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function QuotationEditor() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("saved");
  const [conflict, setConflict] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const docRef = useRef(null);
  const version = useRef(1);
  const timer = useRef(null);
  const saving = useRef(false);
  const pending = useRef(false);

  const adopt = useCallback((data, { resetDoc = false } = {}) => {
    version.current = data.version;
    setQuote(data);
    if (resetDoc) {
      const next = docFrom(data);
      docRef.current = next;
      setDoc(next);
    }
  }, []);

  useEffect(() => {
    setQuote(null);
    setDoc(null);
    client
      .get(`/api/admin/quotations/${quoteId}`)
      .then(({ data }) => adopt(data, { resetDoc: true }))
      .catch((err) => setError(err.response?.status === 404 ? "This quotation was not found." : "Could not load the quotation."));
  }, [quoteId, adopt]);

  const save = useCallback(async () => {
    if (saving.current) {
      pending.current = true;
      return true;
    }
    const current = docRef.current;
    if (!current) return true;
    if (problemsIn(current).any) {
      setStatus("invalid");
      return false;
    }
    saving.current = true;
    setStatus("saving");
    let ok = true;
    try {
      const { data } = await client.put(`/api/admin/quotations/${quoteId}`, { ...current, version: version.current });
      adopt(data);
      setStatus(pending.current ? "saving" : "saved");
    } catch (err) {
      ok = false;
      if (err.response?.status === 409 && err.response.data.latest) {
        setConflict(err.response.data.latest);
        setStatus("conflict");
      } else {
        setStatus("error");
        toast.error(err.response?.data?.error || "Could not save the quotation.", { toastId: "quote-save" });
      }
      pending.current = false;
    } finally {
      saving.current = false;
      if (pending.current) {
        pending.current = false;
        save();
      }
    }
    return ok;
  }, [quoteId, adopt]);

  const change = (patch) => {
    const next = { ...docRef.current, ...patch };
    docRef.current = next;
    setDoc(next);
    if (status === "conflict") return;
    clearTimeout(timer.current);
    setStatus(problemsIn(next).any ? "invalid" : "saving");
    timer.current = setTimeout(save, SAVE_DELAY);
  };
  const changeLine = (id, patch) => change({ lines: docRef.current.lines.map((line) => (line.id === id ? { ...line, ...patch } : line)) });
  const moveLine = (index, delta) => {
    const lines = [...docRef.current.lines];
    const [moved] = lines.splice(index, 1);
    lines.splice(index + delta, 0, moved);
    change({ lines });
  };

  const flush = async () => {
    clearTimeout(timer.current);
    return save();
  };

  useEffect(() => {
    const warn = (event) => {
      if (["saving", "error", "invalid"].includes(status)) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [status]);

  // Actions that act on the saved quotation run after any pending save.
  const act = async (label, request, { resetDoc = true, after } = {}) => {
    if (quote && !quote.locked && !(await flush())) {
      toast.error("Fix the highlighted fields first.");
      return null;
    }
    setBusy(label);
    try {
      const { data } = await request();
      if (after) after(data);
      else adopt(data, { resetDoc });
      return data;
    } catch (err) {
      toast.error(err.response?.data?.error || "That did not work.");
      return null;
    } finally {
      setBusy("");
    }
  };

  const setStatusTo = (target) => act(target, () => client.post(`/api/admin/quotations/${quoteId}/status`, { status: target }));
  const revise = () => act("revise", () => client.post(`/api/admin/quotations/${quoteId}/revise`), { after: (data) => navigate(`/admin/quotations/${data.id}`) });
  const duplicate = () => act("duplicate", () => client.post(`/api/admin/quotations/${quoteId}/duplicate`), { after: (data) => navigate(`/admin/quotations/${data.id}`) });
  const remove = async () => {
    if (!window.confirm(`Delete ${quote.display_number}? This cannot be undone.`)) return;
    try {
      clearTimeout(timer.current);
      await client.delete(`/api/admin/quotations/${quoteId}`);
      navigate("/admin/quotations");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not delete the quotation.");
    }
  };
  const importProject = (projectId) => {
    setDialog(null);
    act("import", () => client.post(`/api/admin/quotations/${quoteId}/import-project`, { project_id: projectId }));
  };
  const addProducts = async (items) => {
    setDialog(null);
    try {
      const { data } = await client.post("/api/products/details", { file_ids: items.map((item) => item.file_id) });
      change({ lines: [...docRef.current.lines, ...items.filter((item) => data[item.file_id]).map((item) => lineFromProduct(item, data[item.file_id]))] });
    } catch {
      toast.error("Could not add those products.");
    }
  };
  const pickCustomer = (customer) => {
    change({ customer_id: customer.id, place_of_supply: customer.state_code || "" });
  };
  const refresh = () => client.get(`/api/admin/quotations/${quoteId}`).then(({ data }) => adopt(data));
  const [editingCustomer, setEditingCustomer] = useState(null);
  // The quotation holds a copy of the customer; editing loads the full record.
  const openCustomer = async () => {
    try {
      const { data } = await client.get(`/api/admin/customers/${quote.customer_id}`);
      setEditingCustomer(data);
    } catch {
      toast.error("Could not load the customer.");
    }
  };
  const openEmail = async () => {
    if (!quote.locked && !(await flush())) {
      toast.error("Fix the highlighted fields first.");
      return;
    }
    setDialog("email");
  };

  const previewPdf = async () => {
    if (quote && !quote.locked && !(await flush())) {
      toast.error("Fix the highlighted fields first.");
      return;
    }
    // Opened straight away so the browser does not block it as a pop-up.
    const win = window.open("", "_blank");
    try {
      const response = await client.get(`/api/admin/quotations/${quoteId}/pdf`, { params: { inline: 1 }, responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (win) win.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      toast.error("Could not build the PDF.");
    }
  };
  const downloadPdf = async () => {
    if (quote && !quote.locked && !(await flush())) {
      toast.error("Fix the highlighted fields first.");
      return;
    }
    setBusy("download");
    await downloadFile(`/api/admin/quotations/${quoteId}/pdf`, {}, `${quote.display_number}.pdf`);
    setBusy("");
  };

  const resolveConflict = () => {
    const latest = conflict;
    setConflict(null);
    adopt(latest, { resetDoc: true });
    setStatus("saved");
  };

  if (error) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-error">{error}</p>
        <Link to="/admin/quotations" className="mb-text-button">
          Back to quotations
        </Link>
      </main>
    );
  }
  if (!quote || !doc) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-muted">Loading quotation...</p>
      </main>
    );
  }

  const locked = quote.locked || !quote.can_change;
  const problems = problemsIn(doc);
  const totals = quote.totals;
  const company = quote.company || {};
  const homeState = company.state_code;
  const inter = homeState && doc.place_of_supply && homeState !== doc.place_of_supply;
  const customer = quote.customer;

  return (
    <main className="mb-page ar-page qt-editor">
      <ToastContainer position="bottom-right" />
      <datalist id="qt-units">
        {UNITS.map((unit) => (
          <option key={unit} value={unit} />
        ))}
      </datalist>

      <header className="ar-project-head">
        <Link to="/admin/quotations" className="mb-icon-button" aria-label="Back to quotations" title="Back to quotations">
          <ArrowLeft size={18} strokeWidth={1.8} />
        </Link>
        <div className="qt-title">
          <h1>{quote.display_number}</h1>
          <StatusBadge status={quote.status} expired={quote.expired} />
          {!locked && <span className={`mb-status is-${status === "saved" ? "saved" : status === "saving" ? "saving" : "error"}`}>{STATUS_TEXT[status]}</span>}
        </div>
        <div className="ar-head-actions">
          <button type="button" className="mb-tool" onClick={previewPdf}>
            <Eye size={15} strokeWidth={1.8} aria-hidden="true" /> Preview
          </button>
          <button type="button" className="mb-tool" onClick={downloadPdf} disabled={busy === "download"}>
            <Download size={15} strokeWidth={1.8} aria-hidden="true" /> {busy === "download" ? "Preparing..." : "PDF"}
          </button>
          {quote.can_change && (
            <button type="button" className="mb-primary" onClick={openEmail}>
              <Mail size={15} strokeWidth={1.9} aria-hidden="true" /> Email
            </button>
          )}
        </div>
      </header>

      {conflict && (
        <div className="mb-banner" role="alert">
          <span>Someone else changed this quotation.</span>
          <button type="button" className="mb-primary" onClick={resolveConflict}>
            Load their version
          </button>
        </div>
      )}
      {!quote.can_change && (
        <div className="qt-notice">
          This is an older revision and cannot change.{" "}
          {quote.revisions[0] && <Link to={`/admin/quotations/${quote.revisions[0].id}`}>Open {quote.revisions[0].display_number}</Link>}
        </div>
      )}
      {quote.can_change && quote.locked && (
        <div className="qt-notice">
          Sent {quote.sent_at ? `on ${new Date(quote.sent_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""} and locked, so the customer's copy never changes.
          Revise it to make changes.
        </div>
      )}

      <div className="ar-project-layout">
        <div className="ar-project-main">
          <section className="ar-card">
            <div className="ar-card-head">
              <h2>Customer</h2>
              {customer && !locked && quote.customer_id && (
                <button type="button" className="mb-tool" onClick={openCustomer}>
                  <Pencil size={14} strokeWidth={1.8} aria-hidden="true" /> Edit customer
                </button>
              )}
            </div>
            {customer ? (
              <div className="qt-party">
                <strong>{customer.company || customer.name}</strong>
                {customer.company && <span>Attn: {customer.name}</span>}
                {customer.billing_address && <span>{customer.billing_address}</span>}
                <span>{[customer.gstin && `GSTIN ${customer.gstin}`, stateLabel(customer.state_code), customer.phone, customer.email].filter(Boolean).join(" · ")}</span>
              </div>
            ) : (
              <p className="ar-muted">Choose who this quotation is for.</p>
            )}
            {!locked && <CustomerPicker value={doc.customer_id} onPick={pickCustomer} disabled={locked} />}
          </section>

          <section className="ar-card">
            <h2>Details</h2>
            <label className="ar-field">
              <span className="ar-field-label">Subject</span>
              <input type="text" value={doc.subject} maxLength={200} onChange={(event) => change({ subject: event.target.value })} disabled={locked} placeholder="e.g. Wallpaper and murals for the Mehta residence" />
            </label>
            <div className="ar-row">
              <label className={`ar-field${problems.quote.includes("quote_date") ? " is-invalid" : ""}`}>
                <span className="ar-field-label">Quotation date</span>
                <input type="date" value={doc.quote_date || ""} onChange={(event) => change({ quote_date: event.target.value })} disabled={locked} />
              </label>
              <label className={`ar-field${problems.quote.includes("valid_until") ? " is-invalid" : ""}`}>
                <span className="ar-field-label">Valid until</span>
                <input type="date" value={doc.valid_until} min={doc.quote_date} onChange={(event) => change({ valid_until: event.target.value })} disabled={locked} />
              </label>
              <label className="ar-field">
                <span className="ar-field-label">Place of supply</span>
                <select value={doc.place_of_supply} onChange={(event) => change({ place_of_supply: event.target.value })} disabled={locked}>
                  <option value="">Choose a state</option>
                  {STATE_OPTIONS.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="ar-muted ar-small">
              {homeState
                ? inter
                  ? `Inter-state supply from ${stateLabel(homeState)}: IGST applies.`
                  : `Supply within ${stateLabel(homeState)}: CGST + SGST apply.`
                : <span className="ar-warning">Set your company's state in <Link to="/admin/company">Company details</Link> so GST is split correctly.</span>}
            </p>
          </section>

          <section className="ar-card">
            <div className="ar-card-head">
              <h2>Lines</h2>
              {!locked && (
                <div className="ar-head-actions">
                  <button type="button" className="mb-tool" onClick={() => setDialog("products")}>
                    <PackageSearch size={15} strokeWidth={1.8} aria-hidden="true" /> Products
                  </button>
                  <button type="button" className="mb-tool" onClick={() => change({ lines: [...docRef.current.lines, newLine("item")] })}>
                    <FilePlus2 size={15} strokeWidth={1.8} aria-hidden="true" /> Line
                  </button>
                  <button type="button" className="mb-tool" onClick={() => change({ lines: [...docRef.current.lines, newLine("charge")] })}>
                    <Truck size={15} strokeWidth={1.8} aria-hidden="true" /> Charge
                  </button>
                  <button type="button" className="mb-tool" onClick={() => setDialog("import")} disabled={busy === "import"}>
                    <FolderInput size={15} strokeWidth={1.8} aria-hidden="true" /> {busy === "import" ? "Importing..." : "From project"}
                  </button>
                </div>
              )}
            </div>
            {!doc.lines.length ? (
              <p className="ar-muted">Add products from the catalogue, your own lines (services, custom work), or charges such as freight and installation.</p>
            ) : (
              <div className="qt-lines">
                {doc.lines.map((line, index) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    index={index}
                    total={doc.lines.length}
                    computed={quote.computed[line.id]}
                    invalid={problems.lines[line.id]}
                    disabled={locked}
                    onChange={(patch) => changeLine(line.id, patch)}
                    onMove={(delta) => moveLine(index, delta)}
                    onRemove={() => change({ lines: docRef.current.lines.filter((entry) => entry.id !== line.id) })}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="ar-card">
            <h2>Notes and terms</h2>
            <label className="ar-field">
              <span className="ar-field-label">Notes for the customer</span>
              <textarea rows={3} value={doc.notes} maxLength={5000} onChange={(event) => change({ notes: event.target.value })} disabled={locked} />
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Terms and conditions</span>
              <textarea rows={6} value={doc.terms} maxLength={5000} onChange={(event) => change({ terms: event.target.value })} disabled={locked} />
            </label>
          </section>
        </div>

        <aside className="ar-project-side">
          <section className="ar-card ar-totals">
            <h2>Totals</h2>
            <dl>
              <div>
                <dt>Sub total</dt>
                <dd>{formatMoney(totals.gross)}</dd>
              </div>
              {totals.line_discount > 0 && (
                <div>
                  <dt>Line discounts</dt>
                  <dd>-{formatMoney(totals.line_discount)}</dd>
                </div>
              )}
            </dl>
            <div className={`qt-discount${problems.quote.includes("discount") ? " is-invalid" : ""}`}>
              <span className="ar-field-label">Overall discount (items only)</span>
              <div>
                <div className="ar-segmented" role="radiogroup" aria-label="Discount type">
                  {[
                    ["percent", "%"],
                    ["amount", "INR"],
                  ].map(([key, label]) => (
                    <button key={key} type="button" role="radio" aria-checked={doc.discount_type === key} className={doc.discount_type === key ? "active" : ""} onClick={() => change({ discount_type: key })} disabled={locked}>
                      {label}
                    </button>
                  ))}
                </div>
                <input type="number" min={0} step="0.01" value={doc.discount_value} onChange={(event) => change({ discount_value: event.target.value })} disabled={locked} placeholder="0" aria-label="Overall discount" />
              </div>
            </div>
            <dl>
              {totals.overall_discount > 0 && (
                <div>
                  <dt>Overall discount</dt>
                  <dd>-{formatMoney(totals.overall_discount)}</dd>
                </div>
              )}
              <div>
                <dt>Taxable value</dt>
                <dd>{formatMoney(totals.taxable)}</dd>
              </div>
              {quote.tax_mode === "inter" ? (
                <div>
                  <dt>IGST</dt>
                  <dd>{formatMoney(totals.igst)}</dd>
                </div>
              ) : (
                <>
                  <div>
                    <dt>CGST</dt>
                    <dd>{formatMoney(totals.cgst)}</dd>
                  </div>
                  <div>
                    <dt>SGST</dt>
                    <dd>{formatMoney(totals.sgst)}</dd>
                  </div>
                </>
              )}
              <div>
                <dt>
                  <label className="mb-toggle">
                    <input type="checkbox" checked={doc.round_off} onChange={(event) => change({ round_off: event.target.checked })} disabled={locked} />
                    <span>Round off</span>
                  </label>
                </dt>
                <dd>{formatMoney(totals.round_off)}</dd>
              </div>
              <div className="ar-total">
                <dt>Grand total</dt>
                <dd>INR {formatMoney(totals.grand_total)}</dd>
              </div>
            </dl>
            {status === "saving" && <p className="ar-muted ar-small">Updating totals...</p>}
          </section>

          <section className="ar-card">
            <h2>Status</h2>
            <div className="qt-actions-grid">
              {quote.can_change && quote.status === "draft" && (
                <button type="button" className="mb-wide-button" onClick={() => setStatusTo("sent")} disabled={Boolean(busy)}>
                  Mark as sent (without email)
                </button>
              )}
              {quote.can_change && quote.status === "sent" && (
                <>
                  <button type="button" className="mb-wide-button" onClick={() => setStatusTo("accepted")} disabled={Boolean(busy)}>
                    Accepted
                  </button>
                  <button type="button" className="mb-wide-button" onClick={() => setStatusTo("rejected")} disabled={Boolean(busy)}>
                    Rejected
                  </button>
                  {!quote.email_log.length && (
                    <button type="button" className="mb-text-button" onClick={() => setStatusTo("draft")} disabled={Boolean(busy)}>
                      Back to draft
                    </button>
                  )}
                </>
              )}
              {quote.can_change && ["accepted", "rejected"].includes(quote.status) && (
                <button type="button" className="mb-text-button" onClick={() => setStatusTo("sent")} disabled={Boolean(busy)}>
                  Undo: back to sent
                </button>
              )}
              {quote.can_change && quote.locked && (
                <button type="button" className="mb-wide-button" onClick={revise} disabled={Boolean(busy)}>
                  <GitBranch size={15} strokeWidth={1.8} aria-hidden="true" /> Revise (R{quote.revisions.length})
                </button>
              )}
              <button type="button" className="mb-wide-button" onClick={duplicate} disabled={Boolean(busy)}>
                <Copy size={15} strokeWidth={1.8} aria-hidden="true" /> Duplicate as new quotation
              </button>
              {quote.status === "draft" && !quote.email_log.length && (
                <button type="button" className="mb-text-button danger" onClick={remove}>
                  <Trash2 size={14} strokeWidth={1.8} aria-hidden="true" /> Delete draft
                </button>
              )}
            </div>
            {quote.project && (
              <p className="ar-muted ar-small">
                From project <Link to={`/projects/${quote.project.id}`}>{quote.project.name}</Link>
              </p>
            )}
          </section>

          {(quote.revisions.length > 1 || quote.email_log.length > 0) && (
            <section className="ar-card">
              {quote.revisions.length > 1 && (
                <>
                  <h2>Revisions</h2>
                  <ul className="qt-history">
                    {quote.revisions.map((entry) => (
                      <li key={entry.id} className={entry.id === quote.id ? "is-current" : ""}>
                        <Link to={`/admin/quotations/${entry.id}`}>{entry.display_number}</Link>
                        <span>
                          {formatDate(entry.quote_date)} · INR {formatMoney(entry.grand_total)}
                        </span>
                        <StatusBadge status={entry.status} />
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {quote.email_log.length > 0 && (
                <>
                  <h2>Emailed</h2>
                  <ul className="qt-history">
                    {quote.email_log.map((entry) => (
                      <li key={entry.at}>
                        <span>{new Date(entry.at).toLocaleString("en-IN")}</span>
                        <span>{[...entry.to, ...(entry.cc || [])].join(", ")}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}
        </aside>
      </div>

      {dialog === "products" && <ProductPicker title="Add products" multiple onClose={() => setDialog(null)} onPick={addProducts} />}
      {dialog === "import" && <ImportProjectDialog onClose={() => setDialog(null)} onImport={importProject} />}
      {dialog === "email" && (
        <EmailDialog
          quote={quote}
          onClose={() => setDialog(null)}
          onSent={(data) => {
            setDialog(null);
            adopt(data, { resetDoc: true });
            toast.success("Quotation emailed.");
          }}
        />
      )}
      {editingCustomer && (
        <CustomerDialog
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSaved={() => {
            setEditingCustomer(null);
            refresh();
          }}
        />
      )}
    </main>
  );
}
